/**
 * Copy, then open. The clipboard write finishes first and the platform opens on a second press
 * inside the confirm dialog; opening first would blur the tab and some browsers then refuse
 * the write.
 */
import type { Draft, PageEvent, PublicPlatform } from "@/lib/api/types";
import { SPONSORED_TAIL } from "@/lib/copy";
import { openUrlFor } from "@/lib/platform";
import { ensureTail, isEdited } from "./logic";

/** True on success. Falls back to a hidden textarea + execCommand when the API is missing. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // try the fallback below
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/** New tab when allowed (the helper stays open behind it), same tab when a blocker says no. */
export function openExternal(url: string): void {
  const win = window.open(url, "_blank");
  if (win) {
    win.opener = null;
    return;
  }
  window.location.assign(url);
}

export interface CopyOutcome {
  ok: boolean;
  /** Exactly what went to the clipboard (or what the customer should copy by hand). */
  copied: string;
  edited: boolean;
  /** The sponsored disclosure line had been deleted and was put back. */
  restoredTail: boolean;
}

export interface CopyArgs {
  text: string;
  title: string;
  draft: Draft | undefined;
  platform: PublicPlatform;
  sponsored: boolean;
  copy?: (text: string) => Promise<boolean>;
  onEvent?: (event: PageEvent) => void;
}

export async function copyForPlatform({
  text,
  title,
  draft,
  platform,
  sponsored,
  copy = copyText,
  onEvent,
}: CopyArgs): Promise<CopyOutcome> {
  let body = text.trim();
  let restoredTail = false;
  if (sponsored) {
    const r = ensureTail(body, SPONSORED_TAIL);
    body = r.text;
    restoredTail = r.restored;
  }
  const edited = isEdited(draft, body, title);
  const ok = await copy(body);
  if (ok) onEvent?.({ kind: "copy", platform_id: platform.id, edited });
  return { ok, copied: body, edited, restoredTail };
}

/** Returns false when the platform has no address (guide-only platforms). */
export function openForPlatform({
  platform,
  onEvent,
  open = openExternal,
}: {
  platform: PublicPlatform;
  onEvent?: (event: PageEvent) => void;
  open?: (url: string) => void;
}): boolean {
  const url = openUrlFor(platform);
  if (!url) return false;
  onEvent?.({ kind: "platform_open", platform_id: platform.id });
  open(url);
  return true;
}
